// Query parameters that are bearer credentials: anyone holding the URL can
// use them until they expire. Presigned S3 URLs (SigV4), CloudFront signed
// URLs, Laravel signed routes and ad-hoc `?token=` links all carry one.
// Compared case-insensitively.
export const CREDENTIAL_PARAM_NAMES: readonly string[] = [
    'x-amz-signature',
    'x-amz-credential',
    'x-amz-security-token',
    'signature',
    'policy',
    'key-pair-id',
    'token',
    'jwt',
    'access_token',
    'id_token',
    'refresh_token',
    'code',
]

const CREDENTIAL_PARAMS = new Set(CREDENTIAL_PARAM_NAMES)

export const REDACTED = '[redacted]'

// Matches `name=value` inside a query string or free text. The name is
// captured so it survives, only the value is replaced. `[?&;\s"'(]` keeps a
// param named `xsignature` from being mistaken for `signature`.
const PARAM_PATTERN = /(^|[?&;\s"'(])([a-z0-9_-]+)=([^&#\s"')]*)/gi

/**
 * Replace the value of every credential query parameter in `input` with
 * `[redacted]`, leaving the rest of the URL readable. Works on a bare URL,
 * a query string, or a longer message that happens to contain one (a
 * console error quoting a failed media request). Non-strings pass through.
 */
export function redactUrlCredentials<T>(input: T): T {
    if (typeof input !== 'string' || input.indexOf('=') === -1) return input

    return input.replace(PARAM_PATTERN, (match, lead: string, name: string, value: string) => {
        if (!value || !CREDENTIAL_PARAMS.has(name.toLowerCase())) return match
        return `${lead}${name}=${REDACTED}`
    }) as T
}

type Breadcrumb = { message?: string; data?: Record<string, unknown> }

/** Sentry `beforeBreadcrumb`: fetch/xhr/navigation crumbs carry full URLs. */
export function redactBreadcrumb<B extends Breadcrumb>(crumb: B): B {
    if (!crumb) return crumb
    if (crumb.message) crumb.message = redactUrlCredentials(crumb.message)
    if (crumb.data) {
        for (const key of ['url', 'from', 'to']) {
            if (typeof crumb.data[key] === 'string') {
                crumb.data[key] = redactUrlCredentials(crumb.data[key] as string)
            }
        }
    }
    return crumb
}

type SentryEvent = {
    message?: string
    request?: { url?: string; query_string?: unknown; headers?: Record<string, string> }
    breadcrumbs?: Breadcrumb[]
    exception?: { values?: Array<{ value?: string }> }
}

/** Sentry `beforeSend`: the page URL, its referrer and any crumbs already attached. */
export function redactSentryEvent<E extends SentryEvent>(event: E): E {
    if (!event) return event
    if (event.message) event.message = redactUrlCredentials(event.message)
    if (event.request) {
        if (event.request.url) event.request.url = redactUrlCredentials(event.request.url)
        if (typeof event.request.query_string === 'string') {
            event.request.query_string = redactUrlCredentials(`?${event.request.query_string}`).slice(1)
        }
        const headers = event.request.headers
        if (headers?.Referer) headers.Referer = redactUrlCredentials(headers.Referer)
    }
    event.breadcrumbs?.forEach(redactBreadcrumb)
    event.exception?.values?.forEach((v) => {
        if (v.value) v.value = redactUrlCredentials(v.value)
    })
    return event
}
